import { useTranslation } from '@exam-room/translations';
import { useDisclosure } from '@shared/hooks';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, generatePath, useLocation } from 'react-router-dom';

import { PATHS } from '../..';
import { Button, LoadingSpinner, PaperSection, SummaryStatsCard } from '../../../components';
import { useExamEvent } from '../../../providers';

import {
  ExamPreviewResultCard,
  ExamPreviewRestartModal,
  ExamPreviewResultPartScoreTable,
  ExamPreviewResultQuestionScoreTable,
} from './components';
import { useGetExamResult } from './hooks';
import { type QConfigStructure } from './types';
import { mapQuestionGradingResults, mapStructureScores } from './utils';

function CompletedPreview() {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const { state: locationState } = useLocation();
  const { t } = useTranslation();

  const [isRestartModalOpen, { close: closeRestartModal, open: openRestartModal }] =
    useDisclosure(false);

  const { examsData } = useExamEvent();

  const examResult = useGetExamResult();

  const handleClick = () => {
    openRestartModal();
  };

  const handleAcceptRestart = () => {
    navigate(generatePath(PATHS.Preview.Instruction, { taskId }));
  };

  const handleCancelRestart = () => {
    closeRestartModal();
  };

  useEffect(() => {
    if (!locationState) {
      navigate(generatePath(PATHS.Preview.Instruction, { taskId }));
    }
  }, [locationState]);

  const structureScores = useMemo(() => {
    if (!examResult || !examsData[0]) return [];

    const examDataQConfigStructureParts = examsData.flatMap(
      (exam) => exam.metadata.qConfigStructureParts ?? [],
    );

    return mapStructureScores(examResult.structureScores, examDataQConfigStructureParts);
  }, [examsData, examResult]);

  const questionGradingResults = useMemo(() => {
    return mapQuestionGradingResults(examResult?.questionGradingResults ?? []);
  }, [examResult]);

  if (!examResult) return <LoadingSpinner />;

  return (
    <div className='flex h-full flex-col items-center gap-6'>
      <ExamPreviewRestartModal
        open={isRestartModalOpen}
        onAccept={handleAcceptRestart}
        onCancel={handleCancelRestart}
      />
      <ExamPreviewResultCard variant={examResult.isPass ? 'passed' : 'failed'}>
        <Button className='w-[420px]' onClick={handleClick}>
          {t('completedPreview.button.startNewExam')}
        </Button>
      </ExamPreviewResultCard>
      <PaperSection title={t('completedPreview.details.title')}>
        <div className='flex flex-col gap-6 p-6'>
          <div className='flex gap-4'>
            <SummaryStatsCard
              title={t('completedPreview.stats.totalQuestions')}
              displayValue={`${examResult.totalQuestion} ${t('completedPreview.stats.questionsUnit')}`}
            />
            <SummaryStatsCard
              title={t('completedPreview.stats.correctAnswers')}
              displayValue={`${examResult.correctAnswerCount} ${t('completedPreview.stats.questionsUnit')}`}
              variant='success'
            />
            <SummaryStatsCard
              title={t('completedPreview.stats.incorrectAnswers')}
              displayValue={`${examResult.incorrectAnswerCount} ${t('completedPreview.stats.questionsUnit')}`}
              variant='error'
            />
            <SummaryStatsCard
              title={t('completedPreview.stats.totalScore')}
              displayValue={`${examResult.totalScore} ${t('completedPreview.stats.scoreUnit')}`}
              variant='info'
            />
          </div>
          <ExamPreviewResultPartScoreTable rows={structureScores} />
          <ExamPreviewResultQuestionScoreTable rows={questionGradingResults} />
        </div>
      </PaperSection>
    </div>
  );
}

const CompletedPreviewRoute = {
  path: PATHS.Preview.Completed,
  element: <CompletedPreview />,
};

export default CompletedPreviewRoute;
